/**
 * Stage 6 — explicit post-purchase favorite prompt (guest pending LS + signed-in API).
 */
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { normalizeLogoChipBackgroundSettings, normalizeLogoChipRimSettings } from 'pi-kiosk-shared';
import { Card } from '../../shared/ui/Card.js';
import { Button } from '../../shared/ui/Button.js';
import { customerQueryKeys } from '../../shared/api/queryKeys.js';
import { useRuntimeConfig } from '../../shared/providers/RuntimeConfigProvider.js';
import { useCustomerSession } from '../../shared/session/CustomerSessionProvider.js';
import { isGloballySignedInChrome } from '../hub/customerHubPaths.js';
import {
  isGuestFavoriteOptedOut,
  isPendingFavoriteSalesPointId,
  stagePendingFavoriteSpContact,
} from '../account/pendingFavoriteSpContacts.js';
import {
  recordSalesPointContactSilent,
  setSalesPointFavorite,
  useSalesPointContactsByToken,
} from '../account/salesPointContactsApi.js';
import { isSalesPointCustomerDiscoveryListable } from '../shop/customerDiscoveryListability.js';
import {
  dismissPostPurchaseFavoritePrompt,
  isPostPurchaseFavoritePromptDismissed,
} from './postPurchaseFavoritePromptSession.js';

export interface PostPurchaseFavoritePromptProps {
  readonly tenantCode: string;
  readonly salesPointId: number;
  readonly salesPointSlug?: string;
  readonly shopDisplayName?: string;
  /** Parent sets true when checkout/payment is COMPLETED. */
  readonly enabled: boolean;
}

function buildShopPathSegment(
  salesPointSlug: string | undefined,
  salesPointId: number,
): string {
  const slug = salesPointSlug?.trim();
  return slug !== undefined && slug.length > 0 ? slug : String(salesPointId);
}

function buildCustomerShopUrl(
  tenantCode: string,
  salesPointSlug: string | undefined,
  salesPointId: number,
): string {
  return `/${tenantCode}/${buildShopPathSegment(salesPointSlug, salesPointId)}`;
}

export function PostPurchaseFavoritePrompt({
  tenantCode,
  salesPointId,
  salesPointSlug,
  shopDisplayName,
  enabled,
}: PostPurchaseFavoritePromptProps): JSX.Element | null {
  const { t } = useTranslation('customer');
  const { apiBaseUrl } = useRuntimeConfig();
  const { session } = useCustomerSession();
  const queryClient = useQueryClient();
  const signedIn = isGloballySignedInChrome({ session });
  const favoritesQuery = useSalesPointContactsByToken(signedIn && enabled);
  const [resolved, setResolved] = useState(false);
  const [acceptPending, setAcceptPending] = useState(false);

  const isEligible = useMemo(() => {
    if (!enabled || resolved) {
      return false;
    }
    if (isPostPurchaseFavoritePromptDismissed(tenantCode, salesPointId)) {
      return false;
    }
    if (
      isSalesPointCustomerDiscoveryListable({
        tenantCode,
        salesPointId,
        salesPointSlug,
        code: salesPointSlug,
      }) !== true
    ) {
      return false;
    }
    if (!signedIn) {
      if (isGuestFavoriteOptedOut({ tenantCode, salesPointId, salesPointSlug })) {
        return false;
      }
      if (isPendingFavoriteSalesPointId(salesPointId)) {
        return false;
      }
      return true;
    }
    if (favoritesQuery.isLoading) {
      return false;
    }
    const match = favoritesQuery.data?.items.find(
      (item) => item.salesPointId === salesPointId,
    );
    return match?.isFavorite !== true && match?.favoriteOptOut !== true;
  }, [
    enabled,
    favoritesQuery.data?.items,
    favoritesQuery.isLoading,
    resolved,
    salesPointId,
    salesPointSlug,
    signedIn,
    tenantCode,
  ]);

  const handleDismiss = useCallback(() => {
    dismissPostPurchaseFavoritePrompt(tenantCode, salesPointId);
    setResolved(true);
  }, [salesPointId, tenantCode]);

  const handleAccept = useCallback(async () => {
    if (acceptPending) {
      return;
    }
    setAcceptPending(true);
    try {
      if (!signedIn) {
        const code = buildShopPathSegment(salesPointSlug, salesPointId);
        stagePendingFavoriteSpContact({
          tenantCode,
          salesPointId,
          salesPointSlug,
          code,
          name: shopDisplayName?.trim() || code,
          tenantName: tenantCode,
          customerShopUrl: buildCustomerShopUrl(tenantCode, salesPointSlug, salesPointId),
          tenantId: 0,
          location: null,
          imageUrl: null,
          tenantLogoUrl: null,
          ...normalizeLogoChipRimSettings({}),
          ...normalizeLogoChipBackgroundSettings({}),
        });
        setResolved(true);
        return;
      }
      await setSalesPointFavorite({
        apiBaseUrl,
        body: { salesPointId, favorite: true },
      });
      void recordSalesPointContactSilent({
        apiBaseUrl,
        body: { salesPointId },
      }).catch(() => {
        /* transport — favorite already set; MEMBERSHIP_BLOCKED swallowed in silent helper */
      });
      const customerKey = session?.customerId ?? 'session';
      await queryClient.invalidateQueries({
        queryKey: customerQueryKeys.salesPointContacts(customerKey),
      });
      setResolved(true);
    } finally {
      setAcceptPending(false);
    }
  }, [
    acceptPending,
    apiBaseUrl,
    queryClient,
    salesPointId,
    salesPointSlug,
    session?.customerId,
    shopDisplayName,
    signedIn,
    tenantCode,
  ]);

  if (!isEligible) {
    return null;
  }

  return (
    <Card padded data-testid="customer-post-purchase-favorite-prompt">
      <h2 className="text-base font-semibold text-[var(--color-on-surface)]">
        {t('checkout.postPurchaseFavorite.title')}
      </h2>
      <p className="mt-2 text-sm text-[var(--color-on-surface-muted)]">
        {t('checkout.postPurchaseFavorite.body')}
      </p>
      <div className="mt-4 flex flex-col gap-3">
        <Button
          intent="primary"
          size="md"
          block
          data-testid="customer-post-purchase-favorite-accept"
          disabled={acceptPending}
          onClick={() => {
            void handleAccept();
          }}
        >
          {t('checkout.postPurchaseFavorite.accept')}
        </Button>
        <Button
          intent="secondary"
          size="md"
          block
          data-testid="customer-post-purchase-favorite-dismiss"
          disabled={acceptPending}
          onClick={handleDismiss}
        >
          {t('checkout.postPurchaseFavorite.dismiss')}
        </Button>
      </div>
    </Card>
  );
}
